import { DatePicker, Input } from 'antd'
import React, { useMemo } from 'react'
import moment from 'moment'
import styled from 'styled-components/macro'

const { TextArea } = Input

const DeliveryForm = ({
  requiredDeliveryDay,
  purchaseOrderNumber,
  notes,
  onDeliveryDayChange,
  onPONumberChange,
  onNotesChange,
  validateDeliveryDay,
  multiSupplierSelected = false,
}) => {
  const handleDateChange = date => {
    if (date && moment.isMoment(date)) {
      // Convert moment object to ISO string
      const isoString = date.toISOString()
      onDeliveryDayChange(isoString)
      // Validate delivery day and show warning if invalid
      if (validateDeliveryDay) {
        validateDeliveryDay(isoString)
      }
    } else {
      onDeliveryDayChange(null)
    }
  }

  const handlePONumberChange = e => {
    const value = e.target.value
    onPONumberChange(value || null)
  }

  const handleNotesChange = e => {
    const value = e.target.value
    onNotesChange(value || null)
  }

  // Disable past dates
  const disabledDate = current => {
    return current && current < moment().startOf('day')
  }

  // Safely convert requiredDeliveryDay to moment object
  // Memoize to prevent unnecessary recalculations during re-renders
  const datePickerValue = useMemo(() => {
    if (!requiredDeliveryDay) {
      return null
    }

    // If it's already a moment object, validate and return it
    if (moment.isMoment(requiredDeliveryDay)) {
      return requiredDeliveryDay.isValid() ? requiredDeliveryDay : null
    }

    // If it's a Date object, convert it to moment
    if (requiredDeliveryDay instanceof Date) {
      const momentDate = moment(requiredDeliveryDay)
      return momentDate.isValid() ? momentDate : null
    }

    // If it's a string (ISO date string), parse it with moment
    if (typeof requiredDeliveryDay === 'string') {
      // Handle empty strings
      if (requiredDeliveryDay.trim() === '') {
        return null
      }
      const momentDate = moment(requiredDeliveryDay)
      // Only return if valid and not an invalid date
      if (momentDate.isValid() && !momentDate.isSame(moment.invalid())) {
        return momentDate
      }
      return null
    }

    // Fallback to null for any other type
    return null
  }, [requiredDeliveryDay])

  return (
    <FormContainer>
      <FormTitle>Delivery Details</FormTitle>
      <FormRow>
        <FormField>
          <Label>
            Delivery Day <Required>*</Required>
          </Label>
          <DatePicker
            style={{ width: '100%' }}
            value={datePickerValue}
            onChange={handleDateChange}
            disabledDate={disabledDate}
            format='DD/MM/YYYY'
            placeholder='Select delivery day'
            allowClear
          />
          <HelpText>
            Select the day you want this order to be delivered
          </HelpText>
        </FormField>
      </FormRow>

      <FormRow>
        <FormField>
          <Label>PO Number (Optional)</Label>
          <Input
            value={multiSupplierSelected ? '' : purchaseOrderNumber || ''}
            onChange={handlePONumberChange}
            placeholder={
              multiSupplierSelected
                ? 'Auto-generated per supplier'
                : 'Leave empty to auto-generate'
            }
            maxLength={30}
            disabled={multiSupplierSelected}
            data-testid='checkout-po-number'
          />
          <HelpText>
            {multiSupplierSelected
              ? 'OMS will automatically generate a unique PO number for each supplier.'
              : 'Purchase order number. If left empty, one will be generated automatically.'}
          </HelpText>
        </FormField>
      </FormRow>

      <FormRow>
        <FormField>
          <Label>Order Notes (Optional)</Label>
          <TextArea
            value={notes || ''}
            onChange={handleNotesChange}
            placeholder='Add any special instructions or notes for suppliers'
            rows={3}
            maxLength={500}
          />
          <HelpText>
            These notes will be included in the order sent to suppliers
          </HelpText>
        </FormField>
      </FormRow>
    </FormContainer>
  )
}

const FormContainer = styled.div`
  background: white;
  border-radius: 4px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`

const FormTitle = styled.h3`
  margin: 0 0 20px 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
`

const FormRow = styled.div`
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #333;
`

const Required = styled.span`
  color: #ff4d4f;
  margin-left: 4px;
`

const HelpText = styled.div`
  font-size: 12px;
  color: #999;
  margin-top: 4px;
`

export default DeliveryForm
